import { StyleSheet } from '@react-pdf/renderer';

import { PDF_FONT_FAMILY_SANS, PDF_FONT_FAMILY_SERIF } from './fonts.ts';

export const PDF_DOCUMENT_COLORS = {
  ink: '#1F1B15',
  inkSoft: '#4A4239',
  inkMuted: '#6B6357',
  ochre: '#B0772B',
  line: '#D9C9A6',
  lineSoft: '#ECDFC4',
  white: '#FFFFFF',
} as const;

export const PDF_SIGNATURE_LAYOUT = {
  width: 148,
  maxHeight: 52,
} as const;

export const pdfStyles = StyleSheet.create({
  page: {
    backgroundColor: PDF_DOCUMENT_COLORS.white,
    color: PDF_DOCUMENT_COLORS.ink,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 9,
    lineHeight: 1.35,
    paddingBottom: 44,
    paddingHorizontal: 52,
    paddingTop: 44,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 116,
  },
  senderColumn: {
    width: 230,
  },
  eyebrow: {
    color: PDF_DOCUMENT_COLORS.ochre,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 7.5,
    fontWeight: 600,
    letterSpacing: 1.6,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  senderName: {
    color: PDF_DOCUMENT_COLORS.ink,
    fontFamily: PDF_FONT_FAMILY_SERIF,
    fontSize: 18,
    fontStyle: 'italic',
    lineHeight: 1.15,
  },
  signatureWrap: {
    height: 60,
    justifyContent: 'flex-end',
    marginTop: 14,
    width: PDF_SIGNATURE_LAYOUT.width,
  },
  signatureSvg: {
    display: 'flex',
  },
  titleColumn: {
    alignItems: 'flex-end',
    width: 240,
  },
  title: {
    color: PDF_DOCUMENT_COLORS.ink,
    fontFamily: PDF_FONT_FAMILY_SERIF,
    fontSize: 38,
    fontStyle: 'italic',
    letterSpacing: 0,
    lineHeight: 1,
    textAlign: 'right',
  },
  titleRule: {
    backgroundColor: PDF_DOCUMENT_COLORS.ochre,
    height: 1,
    marginTop: 12,
    width: 60,
  },
  metadata: {
    marginTop: 14,
    width: 220,
  },
  metadataRow: {
    borderBottomColor: PDF_DOCUMENT_COLORS.lineSoft,
    borderBottomWidth: 0.6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 4,
    paddingTop: 4,
  },
  metadataLabel: {
    color: PDF_DOCUMENT_COLORS.inkMuted,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 7.5,
    fontWeight: 600,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  metadataValue: {
    color: PDF_DOCUMENT_COLORS.ink,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 9,
    textAlign: 'right',
  },
  partySection: {
    flexDirection: 'row',
    marginTop: 22,
    minHeight: 84,
  },
  partyBlock: {
    flexGrow: 1,
    flexShrink: 1,
    paddingRight: 20,
  },
  partyBlockLast: {
    borderLeftColor: PDF_DOCUMENT_COLORS.lineSoft,
    borderLeftWidth: 0.6,
    paddingLeft: 20,
    paddingRight: 0,
  },
  partyName: {
    color: PDF_DOCUMENT_COLORS.ink,
    fontFamily: PDF_FONT_FAMILY_SERIF,
    fontSize: 12,
    fontStyle: 'italic',
    lineHeight: 1.2,
    marginBottom: 5,
  },
  partyLine: {
    color: PDF_DOCUMENT_COLORS.inkSoft,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 9,
    lineHeight: 1.35,
    marginBottom: 1,
  },
  table: {
    marginTop: 20,
  },
  tableHeader: {
    borderBottomColor: PDF_DOCUMENT_COLORS.line,
    borderBottomWidth: 0.8,
    flexDirection: 'row',
    paddingBottom: 6,
    paddingHorizontal: 2,
  },
  tableHeaderText: {
    color: PDF_DOCUMENT_COLORS.ochre,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 7.5,
    fontWeight: 600,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  tableRow: {
    borderBottomColor: PDF_DOCUMENT_COLORS.lineSoft,
    borderBottomWidth: 0.5,
    flexDirection: 'row',
    minHeight: 22,
    paddingHorizontal: 2,
    paddingVertical: 6,
  },
  descriptionColumn: {
    color: PDF_DOCUMENT_COLORS.ink,
    flexGrow: 1,
    flexShrink: 1,
    fontFamily: PDF_FONT_FAMILY_SANS,
    paddingRight: 10,
  },
  quantityColumn: {
    color: PDF_DOCUMENT_COLORS.inkSoft,
    textAlign: 'right',
    width: 52,
  },
  rateColumn: {
    color: PDF_DOCUMENT_COLORS.inkSoft,
    textAlign: 'right',
    width: 86,
  },
  amountColumn: {
    color: PDF_DOCUMENT_COLORS.ink,
    textAlign: 'right',
    width: 94,
  },
  totals: {
    alignSelf: 'flex-end',
    marginTop: 16,
    width: 232,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 4,
    paddingTop: 4,
  },
  totalLabel: {
    color: PDF_DOCUMENT_COLORS.inkMuted,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 8.5,
    fontWeight: 600,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  totalValue: {
    color: PDF_DOCUMENT_COLORS.ink,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 9,
    textAlign: 'right',
  },
  grandTotalRow: {
    borderTopColor: PDF_DOCUMENT_COLORS.line,
    borderTopWidth: 0.8,
    marginTop: 8,
    paddingTop: 10,
  },
  grandTotalLabel: {
    color: PDF_DOCUMENT_COLORS.ochre,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: 1.6,
  },
  grandTotalValue: {
    color: PDF_DOCUMENT_COLORS.ink,
    fontFamily: PDF_FONT_FAMILY_SERIF,
    fontSize: 18,
    fontStyle: 'italic',
    lineHeight: 1,
    textAlign: 'right',
  },
  notesSection: {
    marginTop: 22,
  },
  notesText: {
    color: PDF_DOCUMENT_COLORS.inkSoft,
    fontFamily: PDF_FONT_FAMILY_SANS,
    fontSize: 9,
    lineHeight: 1.45,
  },
});
